import axios, { AxiosInstance } from "axios";
import { useQuery, useMutation, UseQueryOptions, QueryKey, useQueryClient } from "@tanstack/react-query";
import getBITSEndApiMap, { APIMapping, BASE_URL } from "../apiMap";
import { tokenService } from "../services/TokenService";
import { api as axiosServiceApi } from "../services/AxiosService";
import { useEffect, useRef } from "react";

// Helper function to replace path params like :projectId, :kernelId, etc.
function buildUrl(template: string, params: Record<string, string | number>) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? ":" + key),
  );
}

function getAuthToken() {
  const t = tokenService.getToken();
  return t ? `Bearer ${t}` : "";
}

let activeWorkspaceUrl: string | null = null;
let currentProjectId: string | null = null;
const directConnInFlight = new Map<string, Promise<void>>();
const lastDirectConnCheckAt = new Map<string, number>();
const DIRECT_CONN_COOLDOWN_MS = 10_000;

export function setDirectWorkspaceConnection(
  url: string | null, 
  projectId?: string | null
) {
  if (url) {
    console.log(`[jupyterApi] Setting direct connection: ${url} (Project: ${projectId || currentProjectId})`);
  } else if (activeWorkspaceUrl) {
    console.log(`[jupyterApi] Clearing direct connection (was: ${activeWorkspaceUrl})`);
  }

  activeWorkspaceUrl = url;
  if (projectId) {
    currentProjectId = projectId;
  }
}

/**
 * Ensures that the workspaceApi is routed correctly for local agents
 * by verifying the connection status before sensitive calls.
 */
export async function ensureDirectConnection(projectId: string, force = false) {
  if (!projectId) return;

  // If we already have a connection and we're not forcing a refresh, skip
  if (activeWorkspaceUrl && !force) {
    return;
  }

  // Deduplicate + throttle repeated checks (interceptor can call this frequently).
  const now = Date.now();
  const last = lastDirectConnCheckAt.get(projectId) || 0;
  if (!force && now - last < DIRECT_CONN_COOLDOWN_MS) {
    return;
  }
  lastDirectConnCheckAt.set(projectId, now);

  const existing = directConnInFlight.get(projectId);
  if (existing) return existing;

  try {
    const p = (async () => {
      const status = await getAgentSessionStatus(projectId);
      if (status.status === "CONNECTED" && status.workspaceUrl) {
        console.log(`[jupyterApi] Ensuring direct connection to: ${status.workspaceUrl} (forced: ${force})`);
        setDirectWorkspaceConnection(status.workspaceUrl, projectId);
      }
    })();
    directConnInFlight.set(projectId, p);
    await p;
  } catch (error) {
    console.error(`[jupyterApi] Failed to ensure direct connection for ${projectId}:`, error);
  } finally {
    directConnInFlight.delete(projectId);
  }
}

export function applyWorkspaceRouting(instance: AxiosInstance) {
  instance.interceptors.request.use(async (config) => {
    // 1. Determine Project Context
    // Check headers first (Routing Hint), then URL, then current state
    let projectId = (config.headers?.["x-project-id"] as string) || currentProjectId;
    
    if (config.url) {
      // Extraction for /api/jupyter/:projectId/... (handles reviews/submissionId too)
      const projectMatch = config.url.match(/\/api\/jupyter\/(reviews\/[^/]+|[^/]+)/);
      // Extraction for /api/submissions/checkpoint-progress/:id/... (Review Sandbox)
      const submissionMatch = config.url.match(/\/api\/submissions\/checkpoint-progress\/([^/]+)/);

      if (projectMatch && projectMatch[1] && !['api', 'sandbox', 'kernels', 'workspace', 'upload-file'].includes(projectMatch[1])) {
        projectId = projectMatch[1];
      } else if (submissionMatch && submissionMatch[1]) {
        projectId = submissionMatch[1];
      }

      // Extraction for /api/jupyter/api/contents//:projectId/...
      // Guard: root-level content paths like /contents/datasets must NOT be
      // interpreted as project IDs, otherwise we clear a healthy tunnel and
      // mis-route subsequent requests during initial explorer load.
      if (!projectId || projectId === 'api') {
        const contentsMatch = config.url.match(/\/api\/jupyter\/api\/contents\/+([^/?#]+)/);
        const candidate = contentsMatch?.[1];
        const looksLikeProjectId =
          !!candidate &&
          (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate) ||
            candidate.startsWith("reviews/"));
        if (looksLikeProjectId) {
          projectId = candidate;
        }
      }
    }

    // 2. Clear stale tunnel if we definitely switched projects
    if (projectId && currentProjectId && projectId !== currentProjectId) {
      console.log(`[jupyterApi] Project switch detected: ${currentProjectId} -> ${projectId}. Clearing stale tunnel.`);
      activeWorkspaceUrl = null;
    }
    
    if (projectId) {
      currentProjectId = projectId;
    }

    // 3. Resolve Direct Connection if missing
    if (!activeWorkspaceUrl && projectId && projectId !== 'api') {
      await ensureDirectConnection(projectId);
    }

    // 1. Direct Workspace Connection (Cloudflare)
    if (activeWorkspaceUrl) {
      // Forcefully construct the absolute URL to avoid Axios baseURL merging issues
      const baseUrl = activeWorkspaceUrl.replace(/\/$/, "");
      let pathAndSearch = config.url || "";
      
      // Extract just the path and query from the original URL
      if (pathAndSearch.startsWith('http')) {
        try {
          const urlObj = new URL(pathAndSearch);
          // Standardize to only take paths starting from /api to strip platform prefixes like /be
          const apiMatch = urlObj.pathname.match(/\/api\/.*/);
          if (apiMatch) {
            pathAndSearch = apiMatch[0] + urlObj.search;
          } else {
            pathAndSearch = urlObj.pathname + urlObj.search;
          }
        } catch (e) { /* ignore */ }
      }

      if (!pathAndSearch.startsWith("/")) {
        pathAndSearch = "/" + pathAndSearch;
      }

      // Explicitly set the full URL and clear baseURL
      config.baseURL = undefined;
      config.url = baseUrl + pathAndSearch;

      config.headers = config.headers ?? {};
      const auth = getAuthToken();
      if (auth) {
        config.headers["Authorization"] = auth;
      }

      // Inject identity and project context for the agent
      const user = tokenService.getUser();
      if (user?.id) {
        config.headers["x-jupyter-username"] = `user_${user.id}`;
      }
      if (projectId) {
        config.headers["x-project-id"] = projectId;
      }

      // Handle XSRF Token for Jupyter
      if (!config.headers["X-XSRFToken"]) {
        const xsrfCookie = document.cookie.split(";").find(c => c.trim().startsWith("_xsrf="));
        if (xsrfCookie) {
          config.headers["X-XSRFToken"] = decodeURIComponent(xsrfCookie.split("=")[1]);
        }
      }

      // Append token to URL as fallback for some Jupyter endpoints (WebSockets)
      const userToken = tokenService.getToken();
      if (userToken && config.url && !config.url.includes("token=")) {
        const separator = config.url.includes("?") ? "&" : "?";
        config.url = `${config.url}${separator}token=${userToken}`;
      }

      return config;
    }

    // 2. Default Backend JWT Auth
    const auth = getAuthToken();
    if (auth) {
      config.headers = config.headers ?? {};
      config.headers["Authorization"] = auth;
    }
    return config;
  });

  // Response interceptor to clear stale connections on error
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // If a direct connection request fails (CORS error, 504, etc), clear the cached URL 
      // so the next request is forced to re-resolve it from the backend.
      const failedUrl = String(error?.config?.url || "");
      const wasDirectRequest = !!activeWorkspaceUrl && failedUrl.startsWith(activeWorkspaceUrl);
      // Do not clear on generic 404s; notebook UIs can legitimately issue transient 404s
      // while probing paths, which would otherwise cause reconnection thrash.
      if (
        wasDirectRequest &&
        (error.message === 'Network Error' ||
          error.response?.status === 504 ||
          error.response?.status === 530)
      ) {
        console.warn("[workspaceApi] Direct connection failed, clearing stale URL:", activeWorkspaceUrl);
        activeWorkspaceUrl = null;
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

function attachAuthInterceptor(instance: AxiosInstance) {
  return applyWorkspaceRouting(instance);
}

// Axios instance for backend API (orchestration)
export const api: AxiosInstance = axiosServiceApi;

// Axios instance for Direct Workspace Gateway communication.
// Routed through the direct Workspace Agent (Direct Architecture).
// The interceptor dynamically switches the baseURL to the local Cloudflare tunnel or Cloud Agent.
export const workspaceApi: AxiosInstance = attachAuthInterceptor(
  axios.create({
    baseURL: `${BASE_URL.replace(/\/$/, "").replace(/\/api$/, "")}`,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
    timeout: 300000, 
  })
);

/**
 * Manually set the workspace type header for the backend proxy.
 * @deprecated The backend now determines mode from global settings. Use only if explicit override is needed.
 */
export function setWorkspaceTypeHeader(type: "cloud" | "local") {
  workspaceApi.defaults.headers.common["X-Workspace-Type"] = type;
}

export interface CreateWorkspaceResponse {
  isSuccess: boolean;
  message: string;
  data?: {
    folderPath: string;
    notebookPath: string;
    server: {
      name: string;
      full_name: string;
      last_activity: string;
      started: string;
      pending: string | null;
      ready: boolean;
      stopped: boolean;
      url: string;
      user_options: any;
      progress_url: string;
      full_url: string | null;
      full_progress_url: string | null;
      state: {
        object_id: string;
        object_name: string;
      };
    };
    session: {
      id: string;
      path: string;
      name: string;
      type: string;
      kernel: {
        id: string;
        name: string;
        last_activity: string;
        execution_state: string;
        connections: number;
      };
      notebook: {
        path: string;
        name: string;
      };
    };
    kernel: {
      id: string;
      name: string;
      last_activity: string;
      execution_state: string;
      connections: number;
    };
    sessionId: string;
    remainingTime?: number | null;
    totalAllowedTime?: number | null;
    timeUsed?: number | null;
    status?: string;
    isPracticeProject?: boolean; // Flag to differentiate practice projects from normal projects
  };
}

export interface JupyterServerVersion {
  version: string;
}

export interface JupyterUser {
  name: string;
  display_name: string;
  color: string | null;
  initials: string;
  kind: string;
}

export interface JupyterServerStatus {
  connections: number;
  kernels: number;
  last_activity: string;
  started: string;
}

export interface JupyterFileContent {
  name: string;
  path: string;
  last_modified: string;
  created: string;
  content: any;
  format: string | null;
  mimetype: string | null;
  size: number | null;
  writable: boolean;
  type: "directory" | "file" | "notebook";
}

export interface GetContentsResponse {
  name: string;
  path: string;
  last_modified: string;
  created: string;
  content: JupyterFileContent[] | any; // array for directories, content for files
  format: string | null;
  mimetype: string | null;
  size: number | null;
  writable: boolean;
  type: "directory" | "file" | "notebook";
}

export interface JupyterKernel {
  id: string;
  name: string;
  last_activity: string;
  execution_state: string;
  connections: number;
}

export interface JupyterSession {
  id: string;
  path: string;
  name: string;
  type: string;
  kernel: JupyterKernel;
}

export interface JupyterCheckpoint {
  id: string;
  last_modified: string;
}

export interface ExecuteCellRequest {
  code: string;
  timeout?: number;
}

export interface ExecuteCellResponse {
  isSuccess: boolean;
  message: string;
  data: {
    kernel_id: string;
    execution_count: number;
    status: "ok" | "error";
    outputs: Array<{
      output_type: string;
      name?: string;
      text?: string;
      data?: any;
      metadata?: any;
    }>;
    error: string | null;
  };
}

export interface ExecuteNotebookRequest {
  notebook: {
    cells: Array<{
      cell_type: string;
      source: string[];
    }>;
  };
  timeout?: number;
  stopOnError?: boolean;
}

export interface ExecuteNotebookResponse {
  isSuccess: boolean;
  message: string;
  data: {
    kernel_id: string;
    total_cells: number;
    results: Array<{
      cell_index: number;
      execution_count: number;
      outputs: Array<{
        output_type: string;
        name?: string;
        text?: string;
        data?: any;
        metadata?: any;
      }>;
      status: "ok" | "error";
    }>;
  };
}

// Generic API response wrapper from backend
interface ApiResponse<T> {
  isSuccess: boolean;
  message: string;
  data: T;
}

// API Functions
const contentCache = new Map<string, GetContentsResponse>();

/**
 * Clears the content cache for a specific path or the whole cache.
 */
export function clearContentCache(path?: string) {
  if (path) {
    contentCache.delete(path);
  } else {
    contentCache.clear();
  }
}

/**
 * Detects whether the content returned by the Jupyter API is actually an
 * S3-pointer file or a DataLab "Synchronizing..." placeholder that the
 * init.sh writes while a dataset is still downloading in the background.
 *
 * This happens in LOCAL agent mode: datasets are downloaded asynchronously
 * and the file on disk either contains the raw S3 path (e.g.
 * "/mnt/s3-readonly/projects/null/groupfile/…") or the sanitised placeholder
 * text "[DataLab] Synchronizing …" until the real data arrives.
 */
export function isDatasetPointerContent(data: GetContentsResponse): boolean {
  // Only flag small files (< 512 bytes) — real CSV datasets are always larger.
  // Our [DataLab] placeholder is at least 78 bytes. Anything smaller is definitely a real file.
  if (!data || data.type !== "file") return false;
  if (typeof data.size === "number" && (data.size < 76 || data.size > 512)) return false;

  const content = typeof data.content === "string" ? data.content.trim() : "";
  if (!content) return false;

  // Raw S3 mount pointer written before sanitization runs
  if (content.startsWith("/mnt/s3-readonly/")) return true;
  // Sanitized placeholder written by init.sh (lines 64-68)
  if (content.startsWith("[DataLab] Synchronizing")) return true;
  // Generic "Please wait" placeholder
  if (content.includes("Please wait, this file will be available shortly")) return true;

  return false;
}

// Workspace Management
export async function createWorkspace(
  projectId: string,
): Promise<CreateWorkspaceResponse> {
  // Always force a fresh tunnel resolution before creating a workspace to avoid stale URLs
  await ensureDirectConnection(projectId, true);

  // Get the relative path template (e.g. "/api/jupyter/:projectId/createWorkspace")
  const template = APIMapping.jupyterCreateWorkspace;
  
  // Build the relative path (e.g. "/api/jupyter/123/createWorkspace")
  const relativePath = buildUrl(template, { projectId });
  
  // Use workspaceApi instead of platform api to support direct Cloudflare routing for local agents.
  // The interceptor will handle the URL mapping (stripping /api) and Token injection.
  const { data } = await workspaceApi.post<CreateWorkspaceResponse>(relativePath);
  console.log(`[jupyterManagementApi] createWorkspace response:`, data);
  return data;
}

export async function cancelWorkspace(projectId: string) {
  const url = buildUrl(APIMapping.jupyterCancelWorkspace, { projectId });
  // Use workspaceApi for direct routing if available
  const { data } = await workspaceApi.post<ApiResponse<any>>(url);
  return data;
}

export async function wakeAgent(projectId: string) {
  const url = getBITSEndApiMap("agentWake");
  const { data } = await api.post<ApiResponse<any>>(url, { projectId });
  return data;
}

export async function sleepAgent(projectId: string) {
  const url = getBITSEndApiMap("agentSleep");
  const { data } = await api.post<ApiResponse<any>>(url, { projectId });
  return data;
}

export function useCancelProvisioningMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => cancelWorkspace(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ["jupyter", "workspace", "status", projectId] });
    }
  });
}

export const useWakeAgentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => wakeAgent(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ["agent-session-status", projectId] });
    },
  });
};

export const useSleepAgentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => sleepAgent(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ["agent-session-status", projectId] });
    },
  });
};

export const getAgentSessionStatus = async (projectId: string) => {
  const url = buildUrl(getBITSEndApiMap("agentSessionStatus"), { projectId });
  const { data } = await api.get<ApiResponse<{ status: string, workspaceUrl?: string, jupyterToken?: string }>>(url);
  return data.data;
};

export const useAgentSessionStatusQuery = (projectId: string, enabled: boolean = true) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: ["agent-session-status", projectId],
    queryFn: async () => {
      const data = await getAgentSessionStatus(projectId);
      if (data.status === "CONNECTED" && data.workspaceUrl) {
        setDirectWorkspaceConnection(data.workspaceUrl, projectId);
      } else if (data.status === "SLEEPING" || data.status === "OFFLINE") {
        if (currentProjectId === projectId) {
          setDirectWorkspaceConnection(null, projectId);
        }
      }
      return data;
    },
    enabled: !!projectId && enabled,
    staleTime: 60000, // keep cache fresh for a while, since SSE will push updates
  });

  useEffect(() => {
    if (!projectId || !enabled) return;

    const token = tokenService.getToken();
    if (!token) return;

    // Use raw backend API endpoint since EventSource bypasses Axios
    const baseUrl = BASE_URL.replace(/\/$/, "");
    const streamUrl = `${baseUrl}/api/agent/session-status/${encodeURIComponent(projectId)}/stream?token=${token}`;
    
    const eventSource = new EventSource(streamUrl);

    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload && payload.isSuccess && payload.data) {
          const data = payload.data;
          
          // Update the cache directly
          queryClient.setQueryData(["agent-session-status", projectId], data);

          // Process connection state like in queryFn
          if (data.status === "CONNECTED" && data.workspaceUrl) {
            setDirectWorkspaceConnection(data.workspaceUrl, projectId);
          } else if (data.status === "SLEEPING" || data.status === "OFFLINE") {
            if (currentProjectId === projectId) {
              setDirectWorkspaceConnection(null, projectId);
            }
          }
        }
      } catch (e) {
        console.error("[useAgentSessionStatusQuery] SSE parse error", e);
      }
    };

    eventSource.onerror = (err) => {
      console.error("[useAgentSessionStatusQuery] EventSource failed:", err);
      // Fallback to refetching on error
      queryClient.invalidateQueries({ queryKey: ["agent-session-status", projectId] });
    };

    return () => {
      eventSource.close();
    };
  }, [projectId, enabled, queryClient]);

  return queryResult;
};

/**
 * @deprecated The backend now builds the payload and orchestrates initialization directly.
 * Use createWorkspace(projectId, type) instead.
 */
export async function getWorkspaceLaunchPayload(
  projectId: string,
): Promise<ApiResponse<any>> {
  const url = buildUrl(getBITSEndApiMap("workspaceLaunchPayload"), {
    projectId,
  });
  const { data } = await api.get<ApiResponse<any>>(url);
  return data;
}

/**
 * Direct workspace initialization on the Gateway (Cloud or Local).
 * Used when the Frontend orchestrates the launch.
 */
export async function initWorkspaceDirect(
  projectId: string,
  launchPayload: any,
): Promise<ApiResponse<any>> {
  const url = "/api/internal/jupyter/workspace"; // Direct to gateway internal route
  const { data } = await workspaceApi.post<ApiResponse<any>>(url, {
    projectId,
    username: launchPayload.JUPYTERHUB_USER || launchPayload.username, // Handle naming variants
    token: launchPayload.BACKEND_API_TOKEN || launchPayload.token,
    environment: launchPayload,
  });
  return data;
}

export interface LocalRunCommandResponse {
  command: string;
  imageUrl: string;
  note?: string;
}

export async function getLocalRunCommand(
  projectId: string,
): Promise<LocalRunCommandResponse> {
  const url = buildUrl(getBITSEndApiMap("jupyterLocalRunCommand"), {
    projectId,
  });
  const { data } = await api.get<ApiResponse<LocalRunCommandResponse>>(url);
  return data.data;
}

export interface WorkspaceStatusResponse {
  status: "idle" | "provisioning" | "active" | "error";
  progress?: number;
  message?: string;
  timestamp?: number;
  data?: any; // The `CreateWorkspaceResponse['data']` payload when active
}

export async function getWorkspaceStatus(
  projectId: string,
): Promise<WorkspaceStatusResponse> {
  // Interceptor now handles ensureDirectConnection automatically via URL matching

  // Use the backend's management status endpoint since orchestration is now unified.
  const urlTemplate = APIMapping.jupyterGetWorkspaceStatus;
  const url = buildUrl(urlTemplate, { projectId });
  
  try {
    // Use workspaceApi for direct routing if available
    const { data } = await workspaceApi.get<ApiResponse<WorkspaceStatusResponse>>(url);
    if (!data || !data.data) {
       return { status: "idle", message: "Workspace status unavailable" };
    }
    return data.data;
  } catch (error) {
    console.error(`[WorkspaceStatus] Failed to poll status for ${projectId}:`, error);
    return { status: "error", message: "Failed to connect to gateway" };
  }
}

/**
 * Checks the backend Redis cache to see if this user already has an active
 * workspace session for the given project (persisted by internalSandboxStarted).
 * Returns null when no session is cached.
 */
export async function getActiveWorkspaceSession(projectId: string): Promise<{
  sessionId: string | null;
  projectId: string;
  userId: string;
  workspaceType: "cloud" | "local";
  startedAt: string;
  workspaceUrl?: string;
  jupyterToken?: string;
} | null> {
  const url = buildUrl(getBITSEndApiMap("jupyterGetActiveSession"), { projectId });
  const { data } = await api.get<ApiResponse<any>>(url);
  return data.data ?? null;
}

export async function launchReviewSandbox(
  submissionId: string,
): Promise<ApiResponse<any>> {
  // Always force a fresh tunnel resolution before launching a review sandbox to avoid stale URLs
  await ensureDirectConnection(submissionId, true);

  // Get the relative path template (e.g. "/api/submissions/checkpoint-progress/:id/create-review-sandbox")
  const template = APIMapping.jupyterLaunchReviewSandbox;

  // Build the relative path
  const relativePath = buildUrl(template, { id: submissionId });

  // Use workspaceApi instead of platform api to support direct Cloudflare routing.
  const { data } = await workspaceApi.post<ApiResponse<any>>(relativePath);
  console.log(`[jupyterManagementApi] launchReviewSandbox response:`, data);
  return data;
}

export async function getContents(
  projectId: string,
  isLocal?: boolean,
): Promise<GetContentsResponse> {
  // Use path as cache key
  if (contentCache.has(projectId)) {
    return contentCache.get(projectId)!;
  }

  const relPath = buildUrl(APIMapping["workspaceGetContents"], { projectId });
  const looksLikeReviewMissingPrefix =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/|$)/i.test(
      projectId,
    );

  let result: any;
  try {
    const { data } = await workspaceApi.get<any>(relPath);
    result = data;
  } catch (e: any) {
    if (looksLikeReviewMissingPrefix && e?.response?.status === 404) {
      const fallback = buildUrl(APIMapping["workspaceGetContents"], {
        projectId: `reviews/${projectId}`,
      });
      const { data } = await workspaceApi.get<any>(fallback);
      result = data;
    } else {
      throw e;
    }
  }

  let finalData: GetContentsResponse;
  // Support both raw Jupyter response and wrapped ApiResponse JSON
  if (result && typeof result === "object" && "isSuccess" in result && "data" in result) {
    finalData = result.data as GetContentsResponse;
  } else {
    finalData = result as GetContentsResponse;
  }

  // Only cache files with content (don't cache directory listings which change often)
  // Never cache pointer/placeholder files — the real content will arrive once the
  // background dataset download completes, so we must always re-fetch them.
  if (finalData.type !== "directory" && finalData.content !== undefined && !isDatasetPointerContent(finalData)) {
    contentCache.set(projectId, finalData);
  }

  return finalData;
}

export async function getDirectoryContent(
  projectId: string,
  directory: string,
  isLocal?: boolean,
): Promise<GetContentsResponse> {
  const relPath = buildUrl(APIMapping["workspaceGetContentsByDirectory"], {
    projectId,
    directory,
  });
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    projectId,
  );

  let result: any;
  try {
    const { data } = await workspaceApi.get<any>(relPath);
    result = data;
  } catch (e: any) {
    // Review sandboxes live under `reviews/<submissionId>/...`, but some
    // UI routes still pass `submissionId` as `projectId`.
    if (isUuid && e?.response?.status === 404) {
      const fallbackRelPath = buildUrl(APIMapping["workspaceGetContentsByDirectory"], {
        projectId: `reviews/${projectId}`,
        directory,
      });
      const { data } = await workspaceApi.get<any>(fallbackRelPath);
      result = data;
    } else {
      throw e;
    }
  }

  // Support both raw Jupyter response and wrapped ApiResponse JSON
  if (result && typeof result === "object" && "isSuccess" in result && "data" in result) {
    return result.data as GetContentsResponse;
  }
  return result as GetContentsResponse;
}

// Server Information
export async function getServerVersion(): Promise<JupyterServerVersion> {
  const url = APIMapping["workspaceGetServerVersion"];
  const { data } = await workspaceApi.get<JupyterServerVersion>(url);
  return data;
}

export async function getCurrentUser(): Promise<JupyterUser> {
  const url = APIMapping["workspaceGetCurrentUser"];
  const { data } = await workspaceApi.get<JupyterUser>(url);
  return data;
}

export async function getServerStatus(): Promise<JupyterServerStatus> {
  const url = APIMapping["workspaceGetServerStatus"];
  const { data } = await workspaceApi.get<JupyterServerStatus>(url);
  return data;
}

// Session Management
export async function getSession(sessionId: string): Promise<JupyterSession> {
  const url = buildUrl(APIMapping["workspaceGetSession"], {
    session: sessionId,
  });
  const { data } = await workspaceApi.get<JupyterSession>(url);
  return data;
}

export async function listSessions(projectId?: string): Promise<JupyterSession[]> {
  const url = APIMapping["workspaceListSessions"];
  const { data } = await workspaceApi.get<JupyterSession[]>(url, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
  return data;
}

export async function createSession(
  path: string,
  kernelName: string = "python3",
  projectId?: string,
): Promise<JupyterSession> {
  const url = APIMapping["workspaceCreateSession"];
  const { data } = await workspaceApi.post<JupyterSession>(url, {
    path,
    type: "notebook",
    kernel: { name: kernelName },
  }, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
  return data;
}

// Kernel Management
export async function listKernels(projectId?: string): Promise<JupyterKernel[]> {
  const url = APIMapping["workspaceListKernels"];
  const { data } = await workspaceApi.get<JupyterKernel[]>(url, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
  return data;
}

export async function startKernel(
  name: string = "python3",
  path?: string,
  projectId?: string,
): Promise<JupyterKernel> {
  const url = APIMapping["workspaceStartKernel"];
  const { data } = await workspaceApi.post<JupyterKernel>(url, { name, path }, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
  return data;
}

export async function getKernel(kernelId: string, projectId?: string): Promise<JupyterKernel> {
  const url = buildUrl(APIMapping["workspaceGetKernel"], { kernelId });
  const { data } = await workspaceApi.get<JupyterKernel>(url, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
  return data;
}

export async function deleteKernel(kernelId: string, projectId?: string): Promise<void> {
  const url = buildUrl(APIMapping["workspaceDeleteKernel"], { kernelId });
  await workspaceApi.delete(url, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
}

export async function interruptKernel(kernelId: string): Promise<void> {
  const url = buildUrl(APIMapping["workspaceInterruptKernel"], {
    kernelId,
  });
  await workspaceApi.post(url);
}

export async function restartKernel(kernelId: string): Promise<JupyterKernel> {
  const url = buildUrl(APIMapping["workspaceRestartKernel"], { kernelId });
  const { data } = await workspaceApi.post<JupyterKernel>(url);
  return data;
}

// Cell Execution
export async function executeCell(
  kernelId: string,
  request: ExecuteCellRequest,
  projectId?: string,
): Promise<ExecuteCellResponse> {
  const url = buildUrl(APIMapping["workspaceExecuteCell"], { kernelId });
  const { data } = await workspaceApi.post<ExecuteCellResponse>(url, request, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
  return data;
}

export async function executeNotebook(
  kernelId: string,
  request: ExecuteNotebookRequest,
  projectId?: string,
): Promise<ExecuteNotebookResponse> {
  const url = buildUrl(APIMapping["workspaceExecuteNotebook"], {
    kernelId,
  });
  const { data } = await workspaceApi.post<ExecuteNotebookResponse>(url, request, {
    headers: projectId ? { "x-project-id": projectId } : {}
  });
  return data;
}

// Content Management
export async function createContent(
  path: string,
  type: "file" | "directory",
  content?: any,
): Promise<JupyterFileContent> {
  const url = buildUrl(APIMapping["workspaceCreateContents"], { path });
  const { data } = await workspaceApi.post<JupyterFileContent>(url, {
    type,
    content: content || null,
    format: type === "file" ? "text" : null,
  });
  return data;
}

export async function updateContent(
  path: string,
  content: any,
  type: string = "file",
  format: string = "text",
): Promise<JupyterFileContent> {
  const url = buildUrl(APIMapping["workspaceUpdateContents"], { path });

  // Use the same interceptor logic but with a dedicated instance if needed for special content types
  // For now, let's keep it consistent with the new workspaceApi client
  // Invalidate cache on update
  contentCache.delete(path);

  const { data } = await workspaceApi.put<JupyterFileContent>(url, {
    type,
    format,
    content,
  }, {
    headers: { "Content-Type": "application/json" },
    timeout: 600000,
  });
  return data;
}

export async function deleteContent(path: string): Promise<void> {
  contentCache.delete(path);
  const url = buildUrl(APIMapping["workspaceDeleteContents"], { path });
  await workspaceApi.delete(url);
}

export async function renameContent(
  oldPath: string,
  newPath: string,
): Promise<JupyterFileContent> {
  contentCache.delete(oldPath);
  contentCache.delete(newPath);
  const url = buildUrl(APIMapping["workspaceUpdateContents"], {
    path: oldPath,
  });

  const { data } = await workspaceApi.patch<JupyterFileContent>(url, {
    path: newPath,
  });

  return data;
}

// Checkpoint Management
export async function getCheckpoints(
  path: string,
): Promise<JupyterCheckpoint[]> {
  const url = buildUrl(APIMapping["workspaceGetCheckpoints"], { path });
  const { data } = await workspaceApi.get<JupyterCheckpoint[]>(url);
  return data;
}

export async function createCheckpoint(
  path: string,
): Promise<JupyterCheckpoint> {
  const url = buildUrl(APIMapping["workspaceCreateCheckpoint"], { path });
  const { data } = await workspaceApi.post<JupyterCheckpoint>(url);
  return data;
}

export async function restoreCheckpoint(
  path: string,
  checkpointId: string,
): Promise<void> {
  const url = buildUrl(APIMapping["workspaceRestoreCheckpoint"], {
    path,
    checkpointId,
  });
  await workspaceApi.post(url);
}

export async function deleteCheckpoint(
  path: string,
  checkpointId: string,
): Promise<void> {
  const url = buildUrl(APIMapping["workspaceDeleteCheckpoint"], {
    path,
    checkpointId,
  });
  await workspaceApi.delete(url);
}

// React Query Hooks

// Workspace Management Hooks
export function useCreateWorkspaceQuery(projectId: string, enabled = false) {
  return useQuery({
    queryKey: ["jupyter", "workspace", projectId] as QueryKey,
    queryFn: () => createWorkspace(projectId),
    enabled,
    staleTime: 0,
    retry: 1,
  } as UseQueryOptions<CreateWorkspaceResponse, Error, CreateWorkspaceResponse, QueryKey> as any);
}

export function useLocalRunCommandQuery(projectId: string, enabled = false) {
  return useQuery({
    queryKey: ["jupyter", "local-run", projectId] as QueryKey,
    queryFn: () => getLocalRunCommand(projectId),
    enabled,
    staleTime: 0,
    retry: 1,
  });
}

export async function getAgentApiKey(): Promise<{ agentToken: string }> {
  const url = getBITSEndApiMap("sessionAgentToken");
  const { data } = await api.get<ApiResponse<{ agentToken: string }>>(url);
  return data.data;
}

export function useAgentTokenQuery(enabled = false) {
  return useQuery({
    queryKey: ["jupyter", "agent-token"] as QueryKey,
    queryFn: () => getAgentApiKey(),
    enabled,
    staleTime: Infinity, // Permanent token
  });
}

export function useCreateWorkspaceMutation() {
  const queryClient = useQueryClient();
  return useMutation<CreateWorkspaceResponse, Error, { projectId: string }>({
    mutationFn: ({ projectId }) => createWorkspace(projectId),
    onMutate: async ({ projectId }) => {
      await queryClient.cancelQueries({ queryKey: ["jupyter", "workspace", "status", projectId] });
      queryClient.setQueryData(
        ["jupyter", "workspace", "status", projectId],
        (prev: any) => ({
          ...(prev || {}),
          status: "provisioning",
        }),
      );
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["jupyter", "workspace", "status", projectId] });
      queryClient.refetchQueries({ queryKey: ["jupyter", "workspace", "status", projectId], type: "active" });
    }
  });
}

export function useWorkspaceStatusQuery(projectId: string, enabled: boolean = false) {
  return useQuery({
    queryKey: ["jupyter", "workspace", "status", projectId] as QueryKey,
    queryFn: () => getWorkspaceStatus(projectId),
    enabled: !!projectId && enabled,
    retry: 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchInterval: (query) => {
      const status = query?.state?.data?.status;
      if (status === "provisioning") return 2000;
      if (enabled && status === undefined) return 2000;
      // While lifecycle polling is on, keep sampling until we reach a terminal state.
      if (enabled && status !== "idle" && status !== "error") return 2000;
      return false;
    },
  });
}

export function useContentsQuery(projectId: string, isLocal?: boolean, enabled = true) {
  return useQuery<GetContentsResponse>({
    queryKey: ["jupyter", "contents", projectId] as QueryKey,
    queryFn: () => getContents(projectId, isLocal),
    enabled,
    staleTime: 10_000,
  });
}

// Server Information Hooks
export function useServerVersionQuery(enabled = true) {
  return useQuery<JupyterServerVersion>({
    queryKey: ["jupyter", "server", "version"] as QueryKey,
    queryFn: getServerVersion,
    enabled,
    staleTime: 300_000, // 5 minutes
  });
}

export function useCurrentUserQuery(enabled = true) {
  return useQuery<JupyterUser>({
    queryKey: ["jupyter", "user", "current"] as QueryKey,
    queryFn: getCurrentUser,
    enabled,
    staleTime: 60_000, // 1 minute
  });
}

export function useServerStatusQuery(enabled = true) {
  return useQuery<JupyterServerStatus>({
    queryKey: ["jupyter", "server", "status"] as QueryKey,
    queryFn: getServerStatus,
    enabled,
    staleTime: 30_000, // 30 seconds
  });
}

// Session Management Hooks
export function useSessionsQuery(enabled = true) {
  return useQuery<JupyterSession[]>({
    queryKey: ["jupyter", "sessions"] as QueryKey,
    queryFn: () => listSessions(),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateSessionMutation() {
  return useMutation({
    mutationFn: ({ path, kernelName }: { path: string; kernelName?: string }) =>
      createSession(path, kernelName),
  });
}

// Kernel Management Hooks
export function useKernelsQuery(enabled = true) {
  return useQuery<JupyterKernel[]>({
    queryKey: ["jupyter", "kernels"] as QueryKey,
    queryFn: () => listKernels(),
    enabled,
    staleTime: 30_000,
  });
}

export function useKernelQuery(kernelId: string, enabled = true) {
  return useQuery<JupyterKernel>({
    queryKey: ["jupyter", "kernel", kernelId] as QueryKey,
    queryFn: () => getKernel(kernelId),
    enabled: enabled && !!kernelId,
    staleTime: 10_000,
  });
}

export function useStartKernelMutation() {
  return useMutation({
    mutationFn: ({ name, path }: { name?: string; path?: string }) =>
      startKernel(name, path),
  });
}

export function useDeleteKernelMutation() {
  return useMutation<void, Error, string>({
    mutationFn: (kernelId: string) => deleteKernel(kernelId),
  });
}

export function useInterruptKernelMutation() {
  return useMutation({
    mutationFn: interruptKernel,
  });
}

export function useRestartKernelMutation() {
  return useMutation({
    mutationFn: restartKernel,
  });
}

// Cell Execution Hooks
export function useExecuteCellMutation() {
  return useMutation({
    mutationFn: ({
      kernelId,
      request,
    }: {
      kernelId: string;
      request: ExecuteCellRequest;
    }) => executeCell(kernelId, request),
  });
}

export function useExecuteNotebookMutation() {
  return useMutation({
    mutationFn: ({
      kernelId,
      request,
    }: {
      kernelId: string;
      request: ExecuteNotebookRequest;
    }) => executeNotebook(kernelId, request),
  });
}

export function useRenameContentMutation() {
  return useMutation({
    mutationFn: ({ oldPath, newPath }: { oldPath: string; newPath: string }) =>
      renameContent(oldPath, newPath),
  });
}

// Content Management Hooks
export function useCreateContentMutation() {
  return useMutation({
    mutationFn: ({
      path,
      type,
      content,
    }: {
      path: string;
      type: "file" | "directory";
      content?: any;
    }) => createContent(path, type, content),
  });
}

export function useUpdateContentMutation() {
  return useMutation({
    mutationFn: ({
      path,
      content,
      type,
      format,
    }: {
      path: string;
      content: any;
      type?: string;
      format?: string;
    }) => updateContent(path, content, type, format),
  });
}

export function useDeleteContentMutation() {
  return useMutation({
    mutationFn: deleteContent,
  });
}

// Checkpoint Management Hooks
export function useCheckpointsQuery(path: string, enabled = true) {
  return useQuery<JupyterCheckpoint[]>({
    queryKey: ["jupyter", "checkpoints", path] as QueryKey,
    queryFn: () => getCheckpoints(path),
    enabled: enabled && !!path,
    staleTime: 60_000,
  });
}

export function useCreateCheckpointMutation() {
  return useMutation({
    mutationFn: createCheckpoint,
  });
}

export function useRestoreCheckpointMutation() {
  return useMutation({
    mutationFn: ({
      path,
      checkpointId,
    }: {
      path: string;
      checkpointId: string;
    }) => restoreCheckpoint(path, checkpointId),
  });
}

export function useDeleteCheckpointMutation() {
  return useMutation({
    mutationFn: ({
      path,
      checkpointId,
    }: {
      path: string;
      checkpointId: string;
    }) => deleteCheckpoint(path, checkpointId),
  });
}

export function useLaunchReviewSandboxMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) => launchReviewSandbox(submissionId),
    onMutate: async (submissionId) => {
      await queryClient.cancelQueries({ queryKey: ["jupyter", "workspace", "status", submissionId] });
      queryClient.setQueryData(
        ["jupyter", "workspace", "status", submissionId],
        { status: "provisioning" }
      );
    },
    onSuccess: (_, submissionId) => {
      queryClient.invalidateQueries({ queryKey: ["jupyter", "workspace", "status", submissionId] });
      queryClient.refetchQueries({ queryKey: ["jupyter", "workspace", "status", submissionId], type: "active" });
    }
  });
}

export async function stopJupyterServer(): Promise<void> {
  const url = getBITSEndApiMap("jupyterHubStop");
  await api.post(url);
}

export function useStopJupyterServerMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: stopJupyterServer,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["jupyter", "workspace", "status"] });
    }
  });
}

// LTI File Upload Function
export async function uploadFileToJupyter(
  files: File[],
  projectId: string,
  options?: { username?: string; isLocal?: boolean },
): Promise<{ success: boolean; message: string; data?: any }> {
  const url = APIMapping.ltiUploadFile;

  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });
  formData.append("projectId", projectId);

  const { data } = await workspaceApi.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  // Best-effort: push each file into the live Jupyter container directly.
  // If the workspace isn't running the push is silently skipped by jupyter-service.
  if (options?.username) {
    for (const file of files) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        const fileContentBase64 = btoa(binary);
        // Match S3 layout (backend user-uploaded-file): .ipynb at workspace root; datasets/ otherwise.
        const isNotebook = file.name.toLowerCase().endsWith(".ipynb");
        const filePath = options.isLocal
          ? isNotebook
            ? file.name
            : `datasets/${file.name}`
          : isNotebook
            ? `${projectId}/${file.name}`
            : `${projectId}/datasets/${file.name}`;
        await workspaceApi.post('/api/internal/jupyter/push-file', {
          username: options.username,
          filePath,
          fileContentBase64,
        });
      } catch (pushErr) {
        // Non-blocking: file is in S3, the container will sync on next init
        console.warn(`[upload] Failed to live-push ${file.name} to Jupyter:`, pushErr);
      }
    }
  }

  return data;
}

export function useUploadJupyterFileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      files,
      projectId,
      username,
      isLocal,
    }: {
      files: File[];
      projectId: string;
      username?: string;
      isLocal?: boolean;
    }) =>
      uploadFileToJupyter(
        files,
        projectId,
        username ? { username, isLocal } : undefined,
      ),
    onSuccess: (_, { projectId }) => {
      // Invalidate both the Jupyter contents query (Project Explorer) and the legacy files query
      queryClient.invalidateQueries({ queryKey: ["jupyter", "contents", projectId] });
      queryClient.invalidateQueries({ queryKey: ["jupyter", "contents"] });
      queryClient.invalidateQueries({ queryKey: ["files", projectId] });
    },
    onError: (error: any) => {
      console.error("File upload failed:", error);
    },
  });
}

//Enhanced Jupyter Workspace time management

export interface SandboxHeartbeatRequest {
  sessionId: string;
  projectId?: string;    // required for local mode
  isLocal?: boolean;     // route to local agent instead of backend
}

export interface SandboxHeartbeatResponse {
  status: "active" | "idle" | "error";
  remainingTime: number | null;
  totalAllowedTime?: number;
  extension_requested?: boolean;
  isPracticeProject?: boolean;
  workspaceUrl?: string;
  jupyterToken?: string;
}

export interface SandboxStopRequest {
  sessionId?: string;
  projectId?: string;
  username?: string;
}

export interface SandboxStopResponse {
  duration: number; // total cumulative time used (seconds)
  sessionDuration: number; // this session’s duration (seconds)
  status: "completed";
}

export interface CreateWorkspaceEnhancedResponse extends CreateWorkspaceResponse {
  // Augmented time-management fields from enhanced response
  sessionId: string;
  remainingTime: number; // seconds
  totalAllowedTime: number; // seconds
}

export async function createWorkspaceEnhanced(
  projectId: string,
): Promise<CreateWorkspaceEnhancedResponse> {
  const url = buildUrl(getBITSEndApiMap("jupyterWorkspaceCreateEnhanced"), {
    projectId,
  });
  const { data } = await api.post<CreateWorkspaceEnhancedResponse>(url);
  return data;
}

export async function sendSandboxHeartbeat(
  payload: SandboxHeartbeatRequest,
): Promise<SandboxHeartbeatResponse> {
  const url = getBITSEndApiMap("jupyterSandboxHeartbeat");
  const { data } = await api.post<any>(url, { 
    sessionId: payload.sessionId,
    projectId: payload.projectId 
  });
  return data?.data as SandboxHeartbeatResponse;
}

export async function stopSandbox(
  payload: SandboxStopRequest,
): Promise<SandboxStopResponse> {
  // Interceptor now handles ensureDirectConnection automatically via URL matching
  const url = APIMapping.jupyterSandboxStop;
  // Use workspaceApi for direct routing if available
  const { data } = await workspaceApi.post<SandboxStopResponse>(url, payload);
  return data;
}

// Enhanced workspace creation (with time fields)
export function useCreateWorkspaceEnhancedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => createWorkspaceEnhanced(projectId),
    onMutate: async (projectId) => {
      await queryClient.cancelQueries({ queryKey: ["jupyter", "workspace", "status", projectId] });
      queryClient.setQueryData(
        ["jupyter", "workspace", "status", projectId],
        { status: "provisioning" }
      );
    },
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ["jupyter", "workspace", "status", projectId] });
      queryClient.refetchQueries({ queryKey: ["jupyter", "workspace", "status", projectId], type: "active" });
    }
  });
}

// Heartbeat: usually called on a timer; useMutation is appropriate
export function useSandboxHeartbeatMutation() {
  return useMutation({
    mutationFn: (payload: SandboxHeartbeatRequest) =>
      sendSandboxHeartbeat(payload),
  });
}

// Manual stop
export function useStopSandboxMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SandboxStopRequest) => stopSandbox(payload),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["jupyter", "workspace", "status"] });
    }
  });
}

export interface CreateTimeExtensionRequest {
  sandbox_session_id: string;
  reason: string;
  duration_value: string;
  duration_unit: "hours" | "days" | "weeks";
}

export interface CreateTimeExtensionResponse {
  isSuccess: boolean;
  message: string;
  data: {
    id: string;
    sandbox_session_id: string;
    reason: string;
    duration_value: number;
    duration_unit: string;
    status: "pending" | "approved" | "rejected";
    created_at?: string;
    updated_at?: string;
  };
}

export async function createTimeExtensionRequest(
  payload: CreateTimeExtensionRequest,
): Promise<CreateTimeExtensionResponse> {
  const url = getBITSEndApiMap("createTimeExtensionRequest");
  const { data } = await api.post<CreateTimeExtensionResponse>(url, payload);
  return data;
}

export function useCreateTimeExtensionRequestMutation() {
  return useMutation({
    mutationFn: (payload: CreateTimeExtensionRequest) =>
      createTimeExtensionRequest(payload),
  });
}
